<template>
  <div class="section text-left margin-top-0">
    <h3 class="sr-only">Features</h3>
    <ul
      class="fa-ul"
      style="--list-margin-top: var(--spacing-sm)"
    >
      <li
        v-if="(plan || subscription) && planSeats > 0"
        class="margin-bottom-sm padding-left-sm"
      >
        <span class="fa-li">
          <font-awesome-icon
            :icon="context.seats.icon"
            size="lg"
            :class="`${context.seats.iconColor}`"
          />
        </span>
        {{ planSeats }}
        <site-link
          :to="{ name: 'support', hash: '#who-needs-a-seat-on-my-pro-license' }"
          aria-label="Seats are required for all creators or editors using a Font Awesome Pro License. View more details."
          data-balloon-pos="up"
          data-balloon-length="large"
          :class="`link link-underline-dark color-inherit hover-${context.seats.hoverColor}`"
          @click="$posthog.capture('support_link_click', { context: 'plan_card', location: 'seats' })"
        >
          {{ pluralize('Seat', planSeats) }}
        </site-link>

        {{ context.seats.extraSeatCostFormatted ? '+' : '' }}
        <site-link
          v-if="context.seats.extraSeatUpgradeInterval === 1 && context.seats.extraSeatCostFormatted"
          :to="{ name: 'plans', hash: '#overages' }"
          :aria-label="`Extra seats may be purchased for an extra ${context.seats.extraSeatCostFormatted} per seat per ${periodWord}. View more details.`"
          data-balloon-pos="up"
          data-balloon-length="large"
        >
          {{ context.seats.extraSeatCostFormatted }} per extra seat
        </site-link>

        <site-link
          v-if="context.seats.extraSeatUpgradeInterval > 1 && context.seats.extraSeatCostFormatted"
          :to="{ name: 'plans', hash: '#overages' }"
          :aria-label="`Extra seats may be purchased for an extra ${context.seats.extraSeatCostFormatted} per ${context.seats.extraSeatUpgradeInterval} seats per ${periodWord}. View more details.`"
          data-balloon-pos="up"
          data-balloon-length="large"
        >
          {{ context.seats.extraSeatCostFormatted }} / {{ context.seats.extraSeatUpgradeInterval }} seats
        </site-link>
      </li>

      <li
        v-if="!subscription && determinations.isBacker"
        class="margin-bottom-sm padding-left-sm"
      >
        <span class="fa-li">
          <font-awesome-icon
            :icon="context.lifetime5.icon"
            size="lg"
            :class="`${context.lifetime5.iconColor}`"
          />
        </span>
        <span
          aria-label="All the icons in Version 5 are yours to download and use in your projects forever!"
          data-balloon-pos="up"
          data-balloon-length="large"
        >
          Lifetime Version 5 Download
        </span>
      </li>

      <li class="margin-bottom-sm padding-left-sm">
        <span class="fa-li">
          <font-awesome-icon
            :icon="context.icons.icon"
            size="lg"
            :class="`${context.icons.iconColor}`"
          />
        </span>
        <template v-if="!subscription || determinations.active">
          {{ context.icons.label }}
        </template>
        <template v-else>Thousands of Icons</template>
      </li>

      <li
        v-if="context.icons.hasProPlus"
        class="margin-bottom-sm padding-left-sm"
      >
        <span class="fa-li">
          <font-awesome-icon
            :icon="context.proPlus.icon"
            size="lg"
            :class="`${context.icons.iconColor}`"
          />
        </span>
        Includes Pro+ Icons
      </li>

      <li class="margin-bottom-sm padding-left-sm">
        <span class="fa-li">
          <font-awesome-icon
            :icon="context.uploads.icon"
            size="lg"
            :class="`${context.uploads.iconColor}`"
          />
        </span>
        <template v-if="numberOfCustomIcons === -1">Unlimited {{ context.uploads.label }}</template>
        <span
          v-else-if="(!plan && determinations.ended) || determinations.isFree || (determinations.isBacker && !determinations.active)"
          :class="featureStylingUnavailable"
        >
          {{ numberOfCustomIcons }} {{ context.uploads.label }}
        </span>
        <template v-else>{{ numberOfCustomIcons }} {{ context.uploads.label }}</template>
      </li>

      <li class="margin-bottom-sm padding-left-sm">
        <span class="fa-li">
          <font-awesome-icon
            :icon="context.kits.icon"
            size="lg"
            :class="`${context.kits.iconColor}`"
          />
        </span>
        <template v-if="numberOfKits === -1">Unlimited {{ kitsLabel }}</template>

        <span
          v-else-if="(!plan && determinations.ended) || (determinations.isBacker && !determinations.active)"
          :class="featureStylingUnavailable"
        >
          {{ numberOfKits }} {{ kitsLabel }}
        </span>

        <template v-else>{{ numberOfKits }} {{ kitsLabel }}</template>
      </li>

      <li class="margin-bottom-sm padding-left-sm">
        <span class="fa-li">
          <font-awesome-icon
            :icon="context.pageviews.icon"
            size="lg"
            :class="`${context.pageviews.iconColor}`"
          />
        </span>
        <span
          aria-label="Each time one of your pages with a Font Awesome Kit is viewed, a pageview is counted. Pageviews are counted on a monthly basis."
          data-balloon-pos="up"
          data-balloon-length="large"
        >
          <span
            v-if="(!plan && determinations.ended) || (determinations.isBacker && !determinations.active)"
            :class="featureStylingUnavailable"
          >
            {{ context.pageviews.label }}
          </span>
          <template v-else>
            {{ context.pageviews.label }}
          </template>
        </span>
      </li>

      <li
        v-if="!hidesBandwidth"
        class="padding-left-sm"
      >
        <span class="fa-li">
          <font-awesome-icon
            :icon="context.bandwidth.icon"
            size="lg"
            :class="`${context.bandwidth.iconColor}`"
          />
        </span>
        <span
          aria-label="Install, manage, and use Font Awesome Pro via a package in npm, yarn, Python, and Ruby. Package bandwidth is counted on a monthly basis."
          data-balloon-pos="up"
          data-balloon-length="large"
        >
          <span
            v-if="(!plan && determinations.ended) || determinations.isFree || (determinations.isBacker && !determinations.active)"
            :class="featureStylingUnavailable"
          >
            {{ context.bandwidth.label }}
          </span>
          <template v-else>
            {{ context.bandwidth.label }}
          </template>
        </span>
      </li>
    </ul>
  </div>
</template>

<script>
  import get from 'lodash/get'
  import pluralize from 'pluralize'
  import { ComponentContext } from '../mixins/aspects'

  export default {
    name: 'Features',

    mixins: [ComponentContext],

    props: {
      plan: {
        type: Object
      },

      determinations: {
        type: Object,
        required: true
      },

      subscription: {
        type: Object
      },

      seats: {
        type: Number
      }
    },

    data() {
      return {
        featureStylingLost: 'strike margin-right-2xs',
        featureStylingUnavailable: 'text-gray'
      }
    },

    computed: {
      hidesBandwidth() {
        return get(this.currentPlan, ['monthlyBandwidth'], 0) === 0
      },
      currentPlan() {
        return get(this.subscription, ['plan']) || this.plan
      },

      periodWord() {
        return get(this.currentPlan, ['annual'], true) ? 'year' : 'month'
      },

      numberOfCustomIcons() {
        if (this.determinations.preOrderBackerStage === 'shutoff') {
          return 250
        } else if (this.currentPlan) {
          return this.currentPlan.customIcons
        } else {
          return 0
        }
      },

      numberOfKits() {
        if (this.determinations.preOrderBackerStage === 'shutoff') {
          return 20
        } else if (this.currentPlan) {
          return this.currentPlan.kits
        } else {
          return 1
        }
      },

      kitsLabel() {
        const post = pluralize('Kit', this.numberOfKits)

        if (get(this.currentPlan, ['price', 'amount'], 0) > 0 || this.determinations.isBacker) {
          return `Project ${post}`
        } else {
          return `Free Project ${post}`
        }
      },

      planSeats() {
        // Use `??` (nullish coalescing) rather than `||`: an override that
        // sets seats to 0 is a valid effective limit, and `||` would short-
        // circuit past it to the next fallback (typically the customer's
        // paid Stripe seat count), making the customer see e.g. `5` instead
        // of the `0` an admin granted.
        return this.seats ?? get(this, ['plan', 'seats']) ?? get(this, ['subscription', 'seats']) ?? 0
      }
    },

    methods: {
      pluralize
    }
  }
</script>
